// src/components/MenuImportWizard.js
// Menu import: choose source → preview (no DB write) → commit → done.
// The validated items returned by preview are held in state and sent back
// verbatim to /commit; the source is never re-parsed.

import React, { useState, useRef } from 'react';
import api from '../services/api';

const BASE = '/vendors/me/menu/import';
const TABS = [['paste', '📋 Paste JSON'], ['file', '📁 Upload File'], ['url', '🔗 From URL']];
const ACCEPT = '.json,.csv,.xlsx,.xls,.ods';

const card = { background: 'var(--bg3)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' };

function Collapsible({ title, color, rows, limit = 5 }) {
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, limit);
  return (
    <div style={{ ...card, marginTop: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color, marginBottom: 8 }}>{title}</div>
      <div style={{ maxHeight: open ? 260 : 'none', overflowY: 'auto', fontSize: 12, lineHeight: 1.7 }}>
        {shown.map((r, i) => <div key={i}>{r}</div>)}
      </div>
      {rows.length > limit && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, fontSize: 11 }} onClick={() => setOpen(!open)}>
          {open ? 'Show less ▴' : `Show all ${rows.length} ▾`}
        </button>
      )}
    </div>
  );
}

export default function MenuImportWizard({ onClose, onImported }) {
  const [tab, setTab] = useState('paste');
  const [step, setStep] = useState('source');       // source | preview | importing | done
  const [jsonText, setJsonText] = useState('');
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const fileRef = useRef(null);

  const busy = step === 'importing';
  const close = () => { if (!busy) onClose(); };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(json|csv|xlsx|xls|ods)$/i.test(f.name)) { setError('Supported files: .json, .csv, .xlsx, .xls'); return; }
    setFile(f); setError('');
  };

  const runPreview = async () => {
    setLoading(true); setError('');
    try {
      let res;
      if (tab === 'paste') {
        if (!jsonText.trim()) throw new Error('Paste your menu data first');
        let data = jsonText;
        try { data = JSON.parse(jsonText); } catch (_) { /* may be CSV text — server detects */ }
        res = await api.post(`${BASE}/preview`, { data });
      } else if (tab === 'file') {
        if (!file) throw new Error('Choose a file first');
        const fd = new FormData();
        fd.append('file', file);
        res = await api.post(`${BASE}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      } else {
        if (!url.trim()) throw new Error('Enter a URL first');
        res = await api.post(`${BASE}/url`, { url: url.trim() }, { timeout: 60000 });
      }
      setPreview(res.data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Preview failed');
    } finally { setLoading(false); }
  };

  const runCommit = async () => {
    setStep('importing'); setError('');
    try {
      const sys = preview.valid[0]?.source?.system || 'custom';
      const res = await api.post(`${BASE}/commit`, { items: preview.valid, source: { system: sys } }, { timeout: 300000 });
      setResult(res.data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
      setStep('preview');
    }
  };

  const finish = () => { if (result) onImported?.(); onClose(); };

  const s = preview?.stats;
  const skipped = s ? s.errors : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, overflowY: 'auto', padding: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg2)', borderRadius: 16, width: '100%', maxWidth: 760, border: '1px solid var(--border)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>📥 Import Menu</div>
          {!busy && <button onClick={close} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 22, cursor: 'pointer' }}>✕</button>}
        </div>

        <div style={{ padding: 24 }}>
          {error && <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: '#3d1a1a', color: 'var(--red)', fontSize: 13, border: '1px solid var(--red)' }}>❌ {error}</div>}

          {/* ── Step 1: source ── */}
          {step === 'source' && (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {TABS.map(([k, label]) => (
                  <button key={k} onClick={() => { setTab(k); setError(''); }}
                    className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-ghost'}`}>{label}</button>
                ))}
              </div>

              {tab === 'paste' && (
                <textarea value={jsonText} onChange={e => setJsonText(e.target.value)}
                  placeholder="Paste your menu JSON here — supports Foodics, custom restaurant JSON, flat array, and more"
                  style={{ width: '100%', height: 400, fontFamily: 'monospace', fontSize: 12, background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, resize: 'vertical', boxSizing: 'border-box' }} />
              )}

              {tab === 'file' && (
                <div onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={e => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files[0]); }}
                  style={{ border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 12, padding: 48, textAlign: 'center', cursor: 'pointer', background: dragging ? 'var(--bg3)' : 'transparent' }}>
                  <div style={{ fontSize: 32 }}>{file ? '✅' : '📁'}</div>
                  <div style={{ fontWeight: 600, marginTop: 8 }}>{file ? file.name : 'Drop a file here or click to browse'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                    {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '.json  .csv  .xlsx  .xls — up to 50MB'}
                  </div>
                  <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }} onChange={e => pickFile(e.target.files[0])} />
                </div>
              )}

              {tab === 'url' && (
                <>
                  <input className="input" value={url} onChange={e => setUrl(e.target.value)}
                    placeholder="https://your-menu-api.com/menu" style={{ width: '100%', boxSizing: 'border-box' }} />
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
                    The server will fetch this URL directly. Useful for large menus or live API endpoints.
                  </div>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={close}>Cancel</button>
                <button className="btn btn-primary" onClick={runPreview} disabled={loading}>
                  {loading ? 'Reading…' : 'Preview Import →'}
                </button>
              </div>
            </>
          )}

          {/* ── Step 2: preview ── */}
          {step === 'preview' && preview && (
            <>
              <div style={card}>
                <div style={{ fontSize: 11, letterSpacing: 1, color: 'var(--text3)', fontWeight: 700, marginBottom: 10 }}>MENU IMPORT PREVIEW</div>
                <table style={{ fontSize: 13, borderCollapse: 'collapse' }}>
                  <tbody>
                    {[
                      ['Format detected', preview.formatLabel],
                      ['Categories', s.categories.length],
                      ['Products', <span key="p">{s.total}   ✅ {s.valid} valid</span>],
                      ['Modifier groups', s.modifierGroups],
                      ['Images', s.images],
                      ['Warnings', <span key="w">{s.warnings}{s.warnings > 0 && '   ⚠ view below'}</span>],
                      ['Errors (skipped)', <span key="e">{s.errors}{s.errors > 0 && '   ❌ view below'}</span>],
                    ].map(([k, v]) => (
                      <tr key={k}><td style={{ padding: '3px 24px 3px 0', color: 'var(--text2)' }}>{k}:</td><td style={{ fontWeight: 600 }}>{v}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(preview.infos || []).map((m, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text2)', marginTop: 10 }}>ℹ️ {m}</div>
              ))}

              {preview.warnings.length > 0 && (
                <Collapsible title="Warnings (imported anyway)" color="var(--yellow, #f5a623)"
                  rows={preview.warnings.map(w => `⚠ "${w.item}" — ${w.message}`)} />
              )}
              {preview.errors.length > 0 && (
                <Collapsible title="Errors (these will be skipped)" color="var(--red)"
                  rows={preview.errors.map(e => `❌ ${e.item} — ${e.message}${s.valid ? ' (skipped)' : ''}`)} />
              )}

              {s.categories.length > 0 && (
                <div style={{ ...card, marginTop: 12 }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setShowCats(!showCats)}>
                    {showCats ? 'Hide categories ▴' : `Show ${s.categories.length} categories ▾`}
                  </button>
                  {showCats && <div style={{ marginTop: 10, fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {s.categories.map(c => <span key={c} style={{ background: 'var(--bg2)', padding: '3px 10px', borderRadius: 12 }}>{c}</span>)}
                  </div>}
                </div>
              )}

              {s.valid === 0 && (
                <div style={{ marginTop: 14, fontSize: 13, color: 'var(--red)' }}>No valid items to import. Please check the format.</div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost" onClick={() => { setStep('source'); setPreview(null); setError(''); }}>← Back</button>
                <button className="btn btn-ghost" onClick={close}>Cancel</button>
                <button className="btn btn-primary" disabled={s.valid === 0} onClick={runCommit}>
                  Import {s.valid} Items{skipped > 0 ? ` (${skipped} skipped)` : ''} →
                </button>
              </div>
            </>
          )}

          {/* ── Step 3: importing ── */}
          {step === 'importing' && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ fontSize: 32 }}>⏳</div>
              <div style={{ fontWeight: 600, marginTop: 12 }}>Importing {preview.valid.length} items...</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>Please don't close this window.</div>
            </div>
          )}

          {/* ── Step 4: done ── */}
          {step === 'done' && result && (
            <div style={{ padding: '16px 0' }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}>✅ Import Complete</div>
              <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.9 }}>
                <div><strong>{result.total}</strong> items imported successfully</div>
                <div>→ {result.created} new items created</div>
                <div>→ {result.updated} existing items updated</div>
                {(skipped + result.skipped) > 0 && <div>→ {skipped + result.skipped} item{skipped + result.skipped === 1 ? '' : 's'} skipped (see errors)</div>}
                {result.images?.total > 0 && (
                  <div>
                    ✅ {result.images.uploaded} image{result.images.uploaded === 1 ? '' : 's'} uploaded
                    {result.images.skipped > 0 && ` · ${result.images.skipped} already hosted`}
                    {result.images.failed > 0 && <span style={{ color: 'var(--red)' }}> · {result.images.failed} failed (saved without image)</span>}
                  </div>
                )}
              </div>
              {result.failures?.length > 0 && (
                <Collapsible title="Failed to save" color="var(--red)" rows={result.failures.map(f => `❌ ${f.item} — ${f.message}`)} />
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-primary" onClick={finish}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
